/**
 * Responsibility: show a freshly minted credential once, and make clear that
 * it will not be shown again.
 *
 * The server deliberately never returns a channel's target after creating it,
 * so this is the only moment the topic exists on screen. The operator has to
 * subscribe a phone to it, which means the disclosure is necessary - but it is
 * a disclosure, and the dialog says so rather than presenting it as an
 * ordinary field.
 */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { SecretReveal } from '../../api/client';

type SecretRevealDialogProps = {
  reveal: SecretReveal | null;
  onClose: () => void;
};

export function SecretRevealDialog({ reveal, onClose }: SecretRevealDialogProps) {
  const [copied, setCopied] = useState(false);

  if (!reveal) return null;

  const copy = async () => {
    if (!reveal.topic) return;
    try {
      await navigator.clipboard.writeText(reveal.topic);
      setCopied(true);
    } catch {
      // Clipboard access can be refused; the field is selectable either way,
      // so this is not worth an error banner.
      setCopied(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      // Not dismissable by a stray click: closing this loses the topic.
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ fontWeight: 700 }}>{reveal.subscription.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Alert severity="warning">{reveal.note}</Alert>

          {reveal.topic ? (
            <>
              <TextField
                label="Topic"
                value={reveal.topic}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText="Open ntfy on your phone, Subscribe to topic, and paste this."
                onFocus={(event) => event.target.select()}
              />
              <TextField
                label="Or open this link on the phone"
                value={reveal.subscribe_url ?? ''}
                fullWidth
                InputProps={{ readOnly: true }}
                onFocus={(event) => event.target.select()}
              />
              <Typography variant="caption" color="text.secondary">
                Anyone with this topic can read your alerts and send you fake ones. It is not
                shown again - rotate the channel if it ever gets out.
              </Typography>
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {reveal.topic ? (
          <Button size="small" onClick={copy}>{copied ? 'Copied' : 'Copy topic'}</Button>
        ) : null}
        <Button size="small" variant="contained" onClick={onClose}>
          I have subscribed
        </Button>
      </DialogActions>
    </Dialog>
  );
}
